/**
 * useTenantTipo — indica el tipo de la organización activa: 'condominio' | 'cliente'.
 *
 * Sigue el mismo patrón que useMultimoneda: lee el valor de localStorage y se
 * suscribe a cambios vía el evento "storage" (cross-tab) y "tenant-tipo-changed"
 * (mismo tab, p.ej. cuando el CONTADOR/admin cambia de organización).
 *
 * En modo 'cliente' el sistema sustituye el vocabulario de condominio/residente
 * por el de cliente (ver clienteOverrides.js).
 */
import { useState, useEffect } from "react";
import i18n from "../i18n/i18n";
import { clienteOverrides } from "../i18n/clienteOverrides";
import esBase from "../../locales/es.json";
import enBase from "../../locales/en.json";

const BASE = { es: esBase, en: enBase };

function readTipo() {
  return localStorage.getItem("condominio_tipo") || "condominio";
}

/**
 * Aplica (o revierte) el vocabulario de cliente sobre los recursos de i18n.
 * Debe llamarse al iniciar la app y cada vez que cambia el tipo de tenant.
 */
export function applyTenantVocab(tipo) {
  const esCliente = tipo === "cliente";
  ["es", "en"].forEach((lng) => {
    if (esCliente) {
      // deepMerge=true, overwrite=true → fusiona solo las llaves del override.
      i18n.addResourceBundle(lng, "translation", clienteOverrides[lng], true, true);
    } else {
      // Restaurar las traducciones base para revertir cualquier override de
      // cliente que se hubiera aplicado en esta sesión.
      i18n.addResourceBundle(lng, "translation", BASE[lng], true, true);
    }
  });
  // Forzar re-render de los componentes suscritos a i18n.
  i18n.emit("languageChanged", i18n.language);
}

/**
 * Escribe el tipo en localStorage, aplica el vocabulario y notifica a los hooks.
 * Usar en lugar de localStorage.setItem("condominio_tipo", ...) directo.
 *
 * Nota: al pasar de 'cliente' a 'condominio' en la misma sesión los overrides ya
 * fusionados se revierten recargando la página (lo hace el flujo de login/cambio
 * de organización), por eso aquí solo aplicamos hacia 'cliente'.
 */
export function setTenantTipo(tipo) {
  const value = tipo === "cliente" ? "cliente" : "condominio";
  localStorage.setItem("condominio_tipo", value);
  applyTenantVocab(value);
  window.dispatchEvent(new Event("tenant-tipo-changed"));
}

export function useTenantTipo() {
  const [value, setValue] = useState(readTipo);

  useEffect(() => {
    function sync() { setValue(readTipo()); }
    window.addEventListener("storage", sync);
    window.addEventListener("tenant-tipo-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("tenant-tipo-changed", sync);
    };
  }, []);

  return value;
}

/** Helper booleano de conveniencia. */
export function useEsCliente() {
  return useTenantTipo() === "cliente";
}
